<script lang="ts" setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import { deleteProcessRequest } from '@/app/core/services/pending-processes'
import { usePayrollStore } from '@/store/payroll-data'
import PayrollSheet from '../components/PayrollSheet.vue'
import {
  DCard,
  Users01Icon,
  DLoading,
  Loading02Icon,
  DModal,
  DDropdown
} from 'front-library'
import {
  getDataProcessedPayroll,
  closePayroll
} from '../../../core/services/processed-payrolls'
import {
  ReportCustomizedPayroll,
  getReportRequest,
  generateCustomizedPayroll
} from '../../../core/services/reports'
usePayrollChange(() => router.go(-1))

/* store, router, notifications */
const route = useRoute()
const router = useRouter()
const store = useUserStore()
const payrollStore = usePayrollStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos */
const payloadHistoric = ref<any>({})
const dataSheet = ref<any>({})
const loading = ref(false)
const menuOptions = ref<any>([])
const id = ref<any>(route.params.id)
const type = ref<any>(route.params.type)
const idPendingProcesses = ref<any>(route.params?.idPendingProcesses)

/* Datos componente Modal */
const showModal = ref(false)
const showModalExport = ref(false)
const showModalDelete = ref(false)
const titleModal = ref()
const descriptionModal = ref()
const acceptQuestion = ref()
const isClose = ref(false)
const typeAlertModal = ref('')

const message = ref<string>(t('loading.data'))
const urlFile = ref(null)
const refresh = ref()

/*  */
const runAction = (val: any) => {
  switch (val) {
    case 'closePayroll':
      showModal.value = true
      titleModal.value = t('payrollSheet.confirmClose')
      descriptionModal.value = t('modal.description')
      acceptQuestion.value = t('payrollSheet.yesClose')
      isClose.value = true
      break
    case 'delete':
      showModalDelete.value = true
      titleModal.value = t('payrollSheet.confirmDelete')
      descriptionModal.value = t('modal.description')
      acceptQuestion.value = t('payrollSheet.yesDelete')
      break
    case 'export':
      const formatStartDate: any = dataSheet.value.startDate2
      const formatEndDate: any = dataSheet.value.endDate2
      const createReport: ReportCustomizedPayroll = {
        code: 'basics/customized-payroll',
        parameters: {
          payrolls: [dataSheet.value.payroll_id],
          processes: [...dataSheet.value.processCode],
          startDate: formatStartDate,
          endDate: formatEndDate,
          status: dataSheet.value.status,
          concepts: [...dataSheet.value.listconcepts],
          employees: [...dataSheet.value.listemployees],
          showAmount: true,
          showCodes: true,
          showQuantity: true,
          processedPayroll: id.value
        }
      }
      generateReport(createReport)
      break
    default:
      break
  }
}
const setLoading = (val: boolean) => {
  loading.value = val
}

/* Cerrar nomina */
const closedPayroll = async () => {
  showModal.value = false
  loading.value = true
  message.value = t('payrollSheet.close.closingPayroll')
  try {
    const data = {
      processedPayrollId: dataSheet.value.id,
      closedBy: store?.user?.name as string
    }
    await closePayroll(data)
    dataSheet.value = await getDataProcessedPayroll(id.value)
    loading.value = false
    setValuesMenuOptions(true)
    notifications.addNotification({
      title: t('payrollSheet.close.closed'),
      text: t('payrollSheet.close.payrollClosedSuccessfully'),
      type: 'success',
      time: 10000
    })
  } catch (error: any) {
    notificationError(error, '', t, notifications, loading)
  }
}

/* eliminar nómina */
const deletePayroll = async () => {
  loading.value = true
  showModalDelete.value = false
  await deleteProcessRequest(idPendingProcesses.value)
    .then((response: any) => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('pendingProcesses.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      setValuesMenuOptions(true)
      void router.push({ name: 'ListProcessRequests' })
    })
    .catch((error: any) => {
      notificationError(error, '', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  showModalExport.value = false
  showModalDelete.value = false
}

/* opciones del menu */
const setValuesMenuOptions = (error: boolean) => {
  menuOptions.value = [
    {
      name: t('payrollSheet.button.closePayroll'),
      action: 'closePayroll',
      view:
        dataSheet.value.status === 'PRENOMINA' &&
        dataSheet.value.errors.length === 0 &&
        error &&
        store.hasPermission('payroll_closed:create') &&
        dataSheet.value.processCode != 'L'
    },
    {
      name: t('payrollSheet.button.delete'),
      action: 'delete',
      view: dataSheet.value.status === 'PRENOMINA'
    },
    {
      name: t('payrollSheet.button.export'),
      action: 'export',
      view: true
    }
  ]
}

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  message.value = t('payrollSheet.close.generatingReport')
  try {
    const response = await generateCustomizedPayroll(values)
    reportRequest(response)
  } catch (error: any) {
    notificationError(error, '', t, notifications, loading)
  }
}

/* generar reporte */
const reportRequest = async (id: string) => {
  urlFile.value = null
  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('payrollSheet.modal.reportGenerated')
        showModalExport.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('payrollSheet.modal.reportNotGenerated')
        descriptionModal.value = response.errors
        showModalExport.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error) {
      notificationError(error, '', t, notifications, loading)
    }
  }, 5000)
}

onMounted(async () => {
  loading.value = true
  try {
    dataSheet.value = await getDataProcessedPayroll(id.value)
    if (dataSheet.value === undefined) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('pendingProcesses.notification.error'),
        type: 'error',
        time: 10000
      })
      void router.push({ name: 'ListProcessRequests' })
    }
    if (dataSheet.value.payrollCode !== payrollStore.payroll?.code) {
      payrollStore.changeSelectedPayrollByCode(dataSheet.value.payrollCode)
    }
    payloadHistoric.value = {
      type: type.value,
      companyCode: dataSheet.value.companyCode,
      payrollCode: dataSheet.value.payrollCode,
      alterCodesConcepts: dataSheet.value.alterCodesConcepts,
      alterNamesConcepts: dataSheet.value.alterNamesConcepts,
      process: dataSheet.value.processCode,
      endDate: dataSheet.value.endDate.split('-').reverse().join('-'),
      processedPayrollId: id.value,
      processRequestId: dataSheet.value.processRequestId || id.value
    }
    loading.value = false
  } catch (error: any) {
    notificationError(error, '', t, notifications, loading)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <DLoading v-show="loading" :message="message" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="closedPayroll()" />
  <DModal
    :open="showModalExport"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    @closeModal="closeModal">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('payrollSheet.viewFile')" />
    </template>
  </DModal>
  <DModal
    :open="showModalDelete"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="deletePayroll()" />
  <div class="h-full px-5">
    <div class="bg-gray-50 pb-8">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('payrollSheet.create')" />
      </div>
    </div>
    <form class="scroll overflow-auto pr-5" style="height: calc(100% - 5rem)">
      <DCard>
        <div class="grid grid-cols-10">
          <div
            class="col-span-4 xl:col-span-4 2xl:col-span-3 flex flex-col gap-1">
            <div class="flex items-center justify-between pr-3">
              <h1 class="text-lg">{{ dataSheet.payrollName }}</h1>
              <span
                class="text-green-600 bg-green-100 px-3 rounded-xl font-bold py-1">
                {{ dataSheet.statusName }}
              </span>
            </div>
            <div class="flex gap-4">
              <p class="font-medium">
                {{ t('payrollSheet.list.type') }}
                <span
                  class="text-green-600 bg-green-100 px-3 rounded-xl font-bold py-1">
                  {{ dataSheet.type }}
                </span>
              </p>
              <div class="flex gap-4">
                <Users01Icon />
                <p class="font-semibold">{{ dataSheet.employees }}</p>
              </div>
            </div>
            <div class="flex gap-4">
              <p class="font-medium">
                {{ t('payrollSheet.list.period') }}
                <span class="font-bold">
                  {{ dataSheet.startDate }} - {{ dataSheet.endDate }}
                </span>
              </p>
            </div>
          </div>
          <div
            class="flex flex-col xl:col-span-5 2xl:col-span-6 border-0 border-t-1 lg:border-l-4 px-2 lg:border-t-0">
            <h3>{{ t('payrollSheet.list.parametersUsed') }}</h3>
            <div class="flex flex-wrap gap-2">
              <label
                v-for="(param, index) in dataSheet.parameters"
                :key="index"
                class="overflow-auto py-1 px-2 rounded-xl bg-gray-100 text-gray-500 text-sm font-semibold">
                {{ index }}:{{ param }}
              </label>
            </div>
          </div>
          <div class="flex justify-end col-span-1">
            <DDropdown :options="menuOptions" @action="val => runAction(val)" />
          </div>
        </div>
      </DCard>
      <DCard
        v-if="dataSheet?.errors?.length > 0"
        style="
          background-color: rgba(252, 173, 173, 0.288);
          border: 3px solid rgba(255, 165, 165, 0.288);
          margin-top: 24px;
          margin-bottom: 24px;
          max-height: 200px;
          overflow-y: auto;
        ">
        <h1 class="text-red-700" v-html="t('payrollSheet.list.mistakes')" />
        <ul
          class="list-inside ..."
          style="list-style-type: disc"
          v-for="(error, index) in dataSheet.errors"
          :key="index">
          <li
            class="text-red-500 whitespace-break-spaces text-justify text-sm font-semibold">
            {{ error }}
          </li>
        </ul>
      </DCard>
      <Suspense v-if="payloadHistoric.type">
        <PayrollSheet
          :dataHistoric="payloadHistoric"
          :employees="dataSheet.employees"
          @loading="setLoading"
          @isError="setValuesMenuOptions" />
        <template #fallback>
          <div
            class="h-14 border rounded-md bg-gray-200 flex gap-2 items-center px-3 text-gray-500 mt-3">
            <Loading02Icon class="animate-spin text-gray-500" />
            <p>{{ t('payrollSheet.close.loadingPayrollSheet') }}</p>
          </div>
        </template>
      </Suspense>
    </form>
  </div>
</template>
